import { matrixCenterZMean, matrixPQN, matrixZRescale, xGetFromToIndex, xSubtract, xSum, xyMaxYPoint, } from 'ml-spectra-processing';
import hash from 'object-hash';
import { getNormalizedData } from './getNormalizedData.js';
import { integration } from './scaled/integration.js';
import { max } from './scaled/max.js';
import { min } from './scaled/min.js';
import { minMax } from './scaled/minMax.js';
let cache = {};
/**
 * Calculate post-processed data with various transformations and calculations
 * @param spectraProcessor - SpectraProcessor instance
 * @param options - Processing options
 * @returns Post-processed data
 */
export function getPostProcessedData(spectraProcessor, options = {}) {
    const optionsHash = hash(options);
    if (!spectraProcessor.spectra || spectraProcessor.spectra.length === 0) {
        return {};
    }
    const { scale = {}, ids, ranges, calculations, filters = [] } = options;
    const { range, targetID, relative, method = '' } = scale;
    const spectra = spectraProcessor.getSpectra(ids);
    // Check if we can reuse the cache
    if (cache.optionsHash === optionsHash) {
        let validCache = true;
        for (const spectrum of spectra) {
            if (!cache.weakMap?.get(spectrum.normalized))
                validCache = false;
        }
        if (validCache)
            return cache;
    }
    const weakMap = new WeakMap();
    for (const spectrum of spectra) {
        weakMap.set(spectrum.normalized, true);
    }
    const normalizedData = getNormalizedData(spectra);
    for (const filter of filters) {
        switch (filter.name) {
            case 'pqn': {
                normalizedData.matrix = matrixPQN(normalizedData.matrix, filter.options).data;
                break;
            }
            case 'centerMean': {
                normalizedData.matrix = matrixCenterZMean(normalizedData.matrix);
                break;
            }
            case 'rescale': {
                normalizedData.matrix = matrixZRescale(normalizedData.matrix, filter.options);
                break;
            }
            case '':
            case undefined:
                break;
            default:
                throw new Error(`Unknown matrix filter name: ${filter.name}`);
        }
    }
    const normalizedTarget = targetID
        ? spectraProcessor.getSpectrum(targetID)?.normalized
        : spectraProcessor.spectra[0].normalized;
    if (!normalizedTarget) {
        throw new Error('No normalized target found');
    }
    if (method) {
        switch (method.toLowerCase()) {
            case 'min':
                min(normalizedData.matrix, normalizedTarget, range);
                break;
            case 'max':
                max(normalizedData.matrix, normalizedTarget, range);
                break;
            case 'minmax':
                minMax(normalizedData.matrix, normalizedTarget, range);
                break;
            case 'integration':
                integration(normalizedData.matrix, normalizedTarget, range);
                break;
            default:
                throw new Error(`getPostProcessedData: unknown method: ${method}`);
        }
    }
    if (relative) {
        for (let i = 0; i < normalizedData.matrix.length; i++) {
            normalizedData.matrix[i] = xSubtract(normalizedData.matrix[i], normalizedTarget.y);
        }
    }
    const result = normalizedData;
    if (ranges) {
        result.ranges = [];
        for (const spectrum of normalizedData.matrix) {
            const rangesCopy = structuredClone(ranges);
            const yNormalized = spectrum;
            const resultRanges = {};
            result.ranges.push(resultRanges);
            for (const currentRange of rangesCopy) {
                if (currentRange.label) {
                    const fromToIndex = xGetFromToIndex(normalizedTarget.x, currentRange);
                    const deltaX = normalizedTarget.x[1] - normalizedTarget.x[0];
                    currentRange.integration = xSum(yNormalized, fromToIndex) * deltaX;
                    currentRange.maxPoint = xyMaxYPoint({ x: normalizedData.x, y: yNormalized }, fromToIndex);
                    resultRanges[currentRange.label] = currentRange;
                }
            }
        }
    }
    if (calculations && result.ranges) {
        result.calculations = result.ranges.map(() => {
            return {};
        });
        const parameters = Object.keys(result.ranges[0]);
        for (const calculation of calculations) {
            // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
            const callback = new Function(...parameters, `return ${calculation.formula}`);
            for (let i = 0; i < result.ranges.length; i++) {
                const oneRanges = result.ranges[i];
                const values = parameters.map((key) => oneRanges[key].integration);
                result.calculations[i][calculation.label] = callback(...values);
            }
        }
    }
    cache = { ...result, optionsHash, weakMap };
    return cache;
}
//# sourceMappingURL=getPostProcessedData.js.map